import * as R from 'ramda';
import { createContext, useCallback, useContext, useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import PATHS from '../../../../routes/routes.const';
import useSignedContractSelectors from '../hooks/useSignedContractSelectors';
import { signedContractRepository } from '../repository/signedContract.repository';
import { ModalContractPage, ModalContractReadPage } from '../types';
import {
  SignatureContextProps,
  SignatureContextProviderProps,
  SignatureContextType,
  SignatureState,
} from './signature.context.types';

const GEOLOCATION_TIMEOUT = 1000 * 8; // 8 seconds

const initSignatureState: SignatureState = {
  unauthenticatedSign: false,
  signingAs: null,
  contractInfo: null,
  personalInfo: null,
  lastSignImageSize: 0,
  signedAt: new Date(),
  handleGenerateSignedContractPdf: async () =>
    new Promise((res) =>
      res({
        url: '',
        signedAt: new Date(),
      }),
    ),
  selectedSignedContract: {
    id: '',
    version: '',
    userStore: null,
    userBrand: null,
    userBrandSignedAt: null,
    userStoreSignedAt: null,
    order: undefined,
    url: '',
    createdAt: new Date(),
    signatureAdditionalInfo: [],
    authenticityUrl: '',
  },
  page: ModalContractPage.CONTRACT_HISTORY,
  cleanSign: () => {
    return;
  },
  contractUrl: null,
  isLoading: true,
  hasSignInput: false,
};

const defaultParam = {
  ...initSignatureState,
  isReadOnly: false,
  changePageContractReadType: () => undefined,
  changeUploadedSign: () => undefined,
  loadContractUrl: () => undefined,
  isLoadingToFalse: () => undefined,
  isLoadingToTrue: () => undefined,
  changeHandleGenerateSignedContractPdf: () => undefined,
  changeCleanSign: () => undefined,
  changePage: () => undefined,
  loadInitState: () => undefined,
  changeHasSignInput: () => undefined,
  changeSignedAt: () => undefined,
  changeLastSignImageSize: () => undefined,
  changeSigningAs: () => undefined,
  changeContractInfo: () => undefined,
  changePersonalInfo: () => undefined,
  changeUnauthenticatedSign: () => undefined,
};

export const SignatureContext = createContext<SignatureContextProps>(defaultParam);

export const SignatureAction = {
  CHANGE_LAST_SIGN_IMAGE_SIZE: 'CHANGE_LAST_SIGN_IMAGE_SIZE',
  CHANGE_SIGNED_AT: 'CHANGE_SIGNED_AT',
  CHANGE_HANDLE_GENERATE_SIGNED_CONTRACT_PDF: 'CHANGE_HANDLE_GENERATE_SIGNED_CONTRACT_PDF',
  CHANGE_HAS_SIGN_INPUT: 'CHANGE_HAS_SIGN_INPUT',
  LOAD_INIT_STATE: 'LOAD_INIT_STATE',
  CHANGE_PAGE: 'CHANGE_PAGE',
  CHANGE_CLEAN_SIGN_CB: 'CHANGE_CLEAN_SIGN_CB',
  LOAD_CONTRACT_URL: 'LOAD_CONTRACT_URL',
  ISLOADING_TO_FALSE: 'ISLOADING_TO_FALSE',
  ISLOADING_TO_TRUE: 'ISLOADING_TO_TRUE',
  CHANGE_SIGNING_AS: 'CHANGE_SIGNING_AS',
  CHANGE_CONTRACT_INFO: 'CHANGE_CONTRACT_INFO',
  CHANGE_PERSONAL_INFO: 'CHANGE_PERSONAL_INFO',
  CHANGE_UNAUTHENTICATED_SIGN: 'CHANGE_UNAUTHENTICATED_SIGN',
} as const;

export const reducer = (state: SignatureState, action: any) => {
  switch (action.type) {
    case SignatureAction.CHANGE_LAST_SIGN_IMAGE_SIZE: {
      const { lastSignImageSize } = action.payload;
      return {
        ...state,
        lastSignImageSize,
      };
    }
    case SignatureAction.CHANGE_SIGNED_AT: {
      const { signedAt } = action.payload;
      return {
        ...state,
        signedAt,
      };
    }
    case SignatureAction.CHANGE_HAS_SIGN_INPUT: {
      const { hasSignInput } = action.payload;
      return {
        ...state,
        hasSignInput,
      };
    }
    case SignatureAction.LOAD_INIT_STATE: {
      return {
        ...state,
        ...initSignatureState,
      };
    }
    case SignatureAction.CHANGE_HANDLE_GENERATE_SIGNED_CONTRACT_PDF: {
      const { handleGenerateSignedContractPdf } = action.payload;
      return {
        ...state,
        handleGenerateSignedContractPdf,
      };
    }
    case SignatureAction.CHANGE_PAGE: {
      const { page, selectedSignedContract } = action.payload;
      if (page === ModalContractPage.CONTRACT_HISTORY) {
        return {
          ...state,
          isLoading: true,
          contracPdf: null,
          selectedSignedContract: null,
          page,
        };
      }

      const oState = {
        isLoading: false,
        selectedSignedContract: null,
      };

      if (ModalContractReadPage.includes(page)) {
        oState.isLoading = true;
      }

      if (page === ModalContractPage.READ_AND_SIGN_NEW_CONTRACT) {
        return {
          ...state,
          ...oState,
          page,
        };
      }

      if (
        [ModalContractPage.READ_AND_SIGN_CONTRACT, ModalContractPage.READ_CONTRACT_ONLY].includes(
          page,
        )
      ) {
        if (R.isNil(selectedSignedContract) && R.isNil(state.selectedSignedContract)) {
          throw new Error(
            'selectedSignedContract should exist when page READ_AND_SIGN_CONTRACT or READ_CONTRACT_ONLY',
          );
        }
        oState.selectedSignedContract = selectedSignedContract || state.selectedSignedContract;

        return {
          ...state,
          ...oState,
          page,
        };
      }

      if (page === ModalContractPage.CREATE_SIGN_IMAGE) {
        return {
          ...state,
          hasSignInput: false,
          page,
        };
      }

      if (page === ModalContractPage.METHOD_OF_SIGNING) {
        return {
          ...state,
          hasSignInput: false,
          selectedSignedContract: selectedSignedContract || state.selectedSignedContract || null,
          page,
        };
      }

      return {
        ...state,
        isLoading: false,
        page,
      };
    }
    case SignatureAction.CHANGE_CLEAN_SIGN_CB: {
      const { cleanSign } = action.payload;
      return {
        ...state,
        cleanSign,
      };
    }
    case SignatureAction.LOAD_CONTRACT_URL: {
      const { contractUrl } = action.payload;
      return {
        ...state,
        contractUrl,
      };
    }
    case SignatureAction.ISLOADING_TO_FALSE: {
      return {
        ...state,
        isLoading: false,
      };
    }
    case SignatureAction.ISLOADING_TO_TRUE: {
      return {
        ...state,
        isLoading: true,
      };
    }
    case SignatureAction.CHANGE_SIGNING_AS: {
      const { signingAs } = action.payload;
      return {
        ...state,
        signingAs,
      };
    }
    case SignatureAction.CHANGE_CONTRACT_INFO: {
      const { contractInfo } = action.payload;
      return {
        ...state,
        contractInfo,
      };
    }
    case SignatureAction.CHANGE_PERSONAL_INFO: {
      const { personalInfo } = action.payload;
      return {
        ...state,
        personalInfo,
      };
    }
    case SignatureAction.CHANGE_UNAUTHENTICATED_SIGN: {
      const { unauthenticatedSign } = action.payload;
      return {
        ...state,
        unauthenticatedSign,
      };
    }
    default:
      throw new Error('Ação do reducer não encontrada');
  }
};

export const SignatureContextProvider = ({
  children,
  isReadOnly = false,
}: SignatureContextProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initSignatureState);
  const { isAutoSignEnabled, isLastRequiredSigner, hasManualContracts } =
    useSignedContractSelectors();
  const history = useHistory();
  const {
    isLoading,
    contractUrl,
    cleanSign,
    page,
    selectedSignedContract,
    handleGenerateSignedContractPdf,
    hasSignInput,
    signedAt,
    lastSignImageSize,
    signingAs,
    contractInfo,
    personalInfo,
    unauthenticatedSign,
  } = state;

  const changeLastSignImageSize = useCallback(
    (newLastSignImageSize: SignatureContextType.LastSignImageSize) => {
      dispatch({
        type: SignatureAction.CHANGE_LAST_SIGN_IMAGE_SIZE,
        payload: { lastSignImageSize: newLastSignImageSize },
      });
    },
    [],
  );

  const changePage = useCallback(
    (
      newPage: SignatureContextType.Page,
      newSelectedSignedContract: SignatureContextType.SelectedSignedContract = null,
    ) => {
      dispatch({
        type: SignatureAction.CHANGE_PAGE,
        payload: { page: newPage, selectedSignedContract: newSelectedSignedContract || null },
      });
    },
    [],
  );

  const changeCleanSign = useCallback((newCleanSign: SignatureContextType.CleanSign) => {
    dispatch({
      type: SignatureAction.CHANGE_CLEAN_SIGN_CB,
      payload: { cleanSign: newCleanSign },
    });
  }, []);

  const loadContractUrl = useCallback((newContractUrl: SignatureContextType.ContractUrl) => {
    dispatch({ type: SignatureAction.LOAD_CONTRACT_URL, payload: { contractUrl: newContractUrl } });
  }, []);

  const isLoadingToFalse = useCallback(() => {
    dispatch({ type: SignatureAction.ISLOADING_TO_FALSE });
  }, []);

  const isLoadingToTrue = useCallback(() => {
    dispatch({ type: SignatureAction.ISLOADING_TO_TRUE });
  }, []);

  const loadInitState = useCallback(() => {
    dispatch({ type: SignatureAction.LOAD_INIT_STATE });
  }, []);

  const changeHasSignInput = useCallback((newHasSignInput: boolean) => {
    dispatch({
      type: SignatureAction.CHANGE_HAS_SIGN_INPUT,
      payload: { hasSignInput: newHasSignInput },
    });
  }, []);

  const changeSignedAt = useCallback((newSignedAt: Date) => {
    dispatch({
      type: SignatureAction.CHANGE_SIGNED_AT,
      payload: { signedAt: newSignedAt },
    });
  }, []);

  const changeSigningAs = useCallback((newSigningAs: SignatureContextType.SigningAs) => {
    dispatch({
      type: SignatureAction.CHANGE_SIGNING_AS,
      payload: { signingAs: newSigningAs },
    });
  }, []);

  const changeContractInfo = useCallback((newContractInfo: SignatureContextType.ContractInfo) => {
    dispatch({
      type: SignatureAction.CHANGE_CONTRACT_INFO,
      payload: { contractInfo: newContractInfo },
    });
  }, []);

  const changePersonalInfo = useCallback((newPersonalInfo: SignatureContextType.PersonalInfo) => {
    dispatch({
      type: SignatureAction.CHANGE_PERSONAL_INFO,
      payload: { personalInfo: newPersonalInfo },
    });
  }, []);

  const changeUnauthenticatedSign = useCallback((newUnauthenticatedSign: boolean) => {
    dispatch({
      type: SignatureAction.CHANGE_UNAUTHENTICATED_SIGN,
      payload: { unauthenticatedSign: newUnauthenticatedSign },
    });
  }, []);

  const changeHandleGenerateSignedContractPdf = useCallback(
    (getSign: () => Buffer | undefined, refetchValidateConditionsQuery: () => void) => {
      const generateSignedContractPdfCb = async (
        orderId: string,
        personalInfoData: SignatureContextType.PersonalInfo,
      ): Promise<void> => {
        const sign = getSign();
        if (R.isNil(sign)) {
          return;
        }

        isLoadingToTrue();

        let response;

        if (unauthenticatedSign) {
          response = await signedContractRepository().generateSignedContractPdfUnauthenticated(
            sign!,
            contractUrl,
            signingAs,
            selectedSignedContract?.id,
          );
        } else {
          response = await signedContractRepository().generateSignedContractPdf(
            sign!,
            contractUrl,
            signingAs,
            selectedSignedContract?.id,
          );
        }

        const {
          url,
          signedAt: newSignedAt,
          lastSignImageSize: newLastSignImageSize,
          hash,
        } = response.data!;

        const getSignContractInfo = (position?: GeolocationPosition) => ({
          orderId,
          signedContractId: selectedSignedContract?.id!,
          newSignedContract: url!,
          signedAt: newSignedAt,
          lastSignImageSize,
          signingAs,
          lat: position?.coords.latitude,
          lng: position?.coords.longitude,
          accuracy: position?.coords.accuracy,
          name: personalInfoData!.name,
          email: personalInfoData!.email,
          phone: personalInfoData!.phone,
          hashAfterSign: hash,
          sign: sign!,
        });

        const saveSignedContract = async (position?: GeolocationPosition) => {
          if (unauthenticatedSign) {
            await signedContractRepository().signContractUnauthenticated(
              getSignContractInfo(position),
            );
            loadContractUrl(url!);
            changePage(ModalContractPage.CONTRACT_SIGNED);
          } else {
            await signedContractRepository().signContract(getSignContractInfo(position));
            changePage(ModalContractPage.CONTRACT_HISTORY);
            loadContractUrl(null);
          }

          if (isAutoSignEnabled && isLastRequiredSigner(signingAs) && !hasManualContracts) {
            history.push(PATHS.main.orders.path);
          }

          changeLastSignImageSize(newLastSignImageSize);
          changeSignedAt(newSignedAt);
          changePersonalInfo(null);
          refetchValidateConditionsQuery();
        };

        let positionTimer: NodeJS.Timeout;
        let positionResolved = false;

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            if (!positionResolved) {
              positionResolved = true;
              clearTimeout(positionTimer);
              await saveSignedContract(position);
            }
          },
          async () => {
            if (!positionResolved) {
              positionResolved = true;
              clearTimeout(positionTimer);
              await saveSignedContract();
            }
          },
          {
            timeout: GEOLOCATION_TIMEOUT,
          },
        );

        if (positionResolved) {
          return;
        }

        positionTimer = setTimeout(async () => {
          if (!positionResolved) {
            await saveSignedContract();
            positionResolved = true;
          }
        }, 4000);
      };

      dispatch({
        type: SignatureAction.CHANGE_HANDLE_GENERATE_SIGNED_CONTRACT_PDF,
        payload: { handleGenerateSignedContractPdf: generateSignedContractPdfCb },
      });
    },
    [
      changeLastSignImageSize,
      changeSignedAt,
      contractUrl,
      isLoadingToTrue,
      selectedSignedContract,
      signingAs,
      signedAt,
      changePage,
      lastSignImageSize,
      unauthenticatedSign,
      loadContractUrl,
      changePersonalInfo,
    ],
  );

  return (
    <SignatureContext.Provider
      value={{
        isReadOnly,
        signingAs,
        changeSigningAs,
        contractInfo,
        changeContractInfo,
        personalInfo,
        changePersonalInfo,
        signedAt,
        changeSignedAt,
        isLoading,
        contractUrl,
        loadContractUrl,
        isLoadingToFalse,
        isLoadingToTrue,
        handleGenerateSignedContractPdf,
        changeHandleGenerateSignedContractPdf,
        changeUnauthenticatedSign,
        unauthenticatedSign,
        cleanSign,
        changeCleanSign,
        page,
        changePage,
        selectedSignedContract,
        loadInitState,
        hasSignInput,
        changeHasSignInput,
        lastSignImageSize,
        changeLastSignImageSize,
      }}
    >
      {children}
    </SignatureContext.Provider>
  );
};

export const useSignatureContext = () => useContext(SignatureContext);
